import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { User, UserWithToken } from '../models/user.model';
import { authingClient } from '../auth/authing.client';

export const TOKEN_LOCAL_STORAGE_KEY = "token";
export const EMAIL_LOCAL_STORAGE_KEY = "email";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private user = new BehaviorSubject<User | null>(null);
  user$ = this.user.asObservable();
  isLoggedIn$: Observable<boolean> = this.user$.pipe(map(Boolean));

  constructor(private router: Router) {
    this.checkCurrentUser();
  }

  private async checkCurrentUser() {
    const token = localStorage.getItem(TOKEN_LOCAL_STORAGE_KEY);
    if (token) {
      this.pushNewUser(token);
    }
  }

  login(email: string, password: string): Observable<string> {
    return new Observable(observer => {
      authingClient.loginByEmail(email, password)
        .then(user => {
          try {
            const token = user.token || '';
            const email = user.email || '';

            if (!token) {
              throw new Error('No token received from Authing');
            }

            this.saveTokenToLocalStore(token);
            this.saveEmailToLocalStore(email);
            this.pushNewUser(token);
            observer.next(token);
            observer.complete();
          } catch (e) {
            console.error('Error processing login data:', e);
            observer.error(e);
          }
        })
        .catch(err => {
          console.error('Authing login failed:', err);
          observer.error(err)
        });
    });
  }

  logout() {
    localStorage.removeItem(TOKEN_LOCAL_STORAGE_KEY);
    localStorage.removeItem(EMAIL_LOCAL_STORAGE_KEY);
    this.user.next(null);
    this.router.navigateByUrl('/authentication/login');
  }

  private saveTokenToLocalStore(token: string) {
    localStorage.setItem(TOKEN_LOCAL_STORAGE_KEY, token);
  }

  private saveEmailToLocalStore(email: string) {
    localStorage.setItem(EMAIL_LOCAL_STORAGE_KEY, email);
  }

  private pushNewUser(token: string) {
    this.user.next(this.decodeToken(token));
  }

  private decodeToken(userToken: string): UserWithToken {
    const payload = JSON.parse(window.atob(userToken.split(".")[1])) as User;
    return { ...payload, token: userToken } as UserWithToken;
  }

  isLoggedIn(): Observable<boolean> {
    return new Observable(observer => {
      const token = localStorage.getItem(TOKEN_LOCAL_STORAGE_KEY);
      observer.next(!!token);
      observer.complete();
    });
  }

  getUserRole(): string | null {
    const user = this.user.getValue();
    return user?.role || null;
  }
}
